/**
 * Gets the config for the intl object for a given feature category and key in the repackaging GTM context
 * @param categoryKey - The category key, whether for tooltips or list
 * @param featureKey - The specific feature key (e.g. 'collaborators', 'boards')
 * @returns The config for the intl object
 */
export const getRepackagingGtmIntlObject = (
  categoryKey: string,
  featureKey: string,
) => {
  if (categoryKey === 'feature-tooltips-repackaging-gtm') {
    switch (featureKey) {
      case 'collaborators':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.collaborators',
          defaultMessage:
            'Collaborators include members, guests, and pending invitations.',
          description:
            'Collaborators include members, guests, and pending invitations.',
        };
      case 'boards':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.boards',
          defaultMessage:
            'Organize and manage your projects within your Workspace.',
          description:
            'Organize and manage your projects within your Workspace.',
        };
      case 'unlimited-powerups':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.unlimited-powerups',
          defaultMessage:
            'Add board features and connect to apps you already use like Google Drive, Slack, and more.',
          description:
            'Add board features and connect to apps you already use like Google Drive, Slack, and more.',
        };
      case 'automation-1':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.automation-1',
          defaultMessage:
            'Create powerful board automations and actions for your Workspace, with no coding required.',
          description:
            'Create powerful board automations and actions for your Workspace, with no coding required.',
        };
      case 'automation-definition':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.automation-definition',
          defaultMessage:
            'An automation run” is anytime a card button, board button, rule, or other automation takes place. For example, each time a card button is pressed, it’s one automation run.',
          description:
            'An automation run” is anytime a card button, board button, rule, or other automation takes place. For example, each time a card button is pressed, it’s one automation run.',
        };
      case 'custom-backgrounds':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.custom-backgrounds',
          defaultMessage:
            'Upload the perfect backgrounds from your computer or phone for your boards.',
          description:
            'Upload the perfect backgrounds from your computer or phone for your boards.',
        };
      case 'stickers-emojis':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.stickers-emojis',
          defaultMessage:
            'Add flair to your cards with super awesome sticker packs.',
          description:
            'Add flair to your cards with super awesome sticker packs.',
        };
      case 'saved-searches':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.saved-searches',
          defaultMessage:
            'Save your most frequently used searches for easy access.',
          description:
            'Save your most frequently used searches for easy access.',
        };
      case 'custom-fields':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.custom-fields',
          defaultMessage:
            'Add custom dropdowns, checkboxes, and more to your cards',
          description:
            'Add custom dropdowns, checkboxes, and more to your cards',
        };
      case 'data-exports':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.data-exports',
          defaultMessage: 'Export board data.',
          description: 'Export board data.',
        };
      case 'dashboard':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.dashboard',
          defaultMessage:
            'Dig into the data with tiles to see workload, timing, and more about your Workspace.',
          description:
            'Dig into the data with tiles to see workload, timing, and more about your Workspace.',
        };
      case 'timeline':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.timeline',
          defaultMessage:
            'Visually plan time and workload, similar to a Gantt chart.',
          description:
            'Visually plan time and workload, similar to a Gantt chart.',
        };
      case 'calendar':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.calendar',
          defaultMessage: 'Check out all the important dates on your cards.',
          description: 'Check out all the important dates on your cards.',
        };
      case 'table':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.table',
          defaultMessage:
            'Display cards as rows and columns, like a spreadsheet.',
          description: 'Display cards as rows and columns, like a spreadsheet.',
        };
      case 'map':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.map',
          defaultMessage: 'See all card locations on a map.',
          description: 'See all card locations on a map.',
        };
      case 'advanced-checklists':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.advanced-checklists',
          defaultMessage:
            'Add due dates and assign collaborators to individual checklist items.',
          description:
            'Add due dates and assign collaborators to individual checklist items.',
        };
      case 'workspace-board-templates':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.workspace-board-templates',
          defaultMessage:
            'Create and save custom board templates for your Workspace to use.',
          description:
            'Create and save custom board templates for your Workspace to use.',
        };
      case 'board-collections':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.board-collections',
          defaultMessage:
            'Easily group boards together whether by Workspace, department, or major project.',
          description:
            'Easily group boards together whether by Workspace, department, or major project.',
        };
      case 'observers':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.observers',
          defaultMessage: 'Limit certain people’s actions on your boards.',
          description: 'Limit certain people’s actions on your boards.',
        };
      case 'admin-security-features':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.admin-security-features',
          defaultMessage:
            'Enable two-factor authentication, advanced admin permissions, domain-restricted invites, deactivate members, and Google apps sign-on.',
          description:
            'Enable two-factor authentication, advanced admin permissions, domain-restricted invites, deactivate members, and Google apps sign-on.',
        };
      case 'priority-support':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.priority-support',
          defaultMessage:
            'Get your questions answered within one business day, guaranteed.',
          description:
            'Get your questions answered within one business day, guaranteed.',
        };
      case 'unlimited-workspaces':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.unlimited-workspaces',
          defaultMessage:
            'With Enterprise, your organization can have multiple Workspaces—all with the features of Premium.',
          description:
            'With Enterprise, your organization can have multiple Workspaces—all with the features of Premium.',
        };
      case 'powerup-administration':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.powerup-administration',
          defaultMessage: 'Control which Power-Ups can be added to boards.',
          description: 'Control which Power-Ups can be added to boards.',
        };
      case 'saml-sso':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.saml-sso',
          defaultMessage:
            'Enterprise organizations can set up more admin features with Atlassian Access.',
          description:
            'Enterprise organizations can set up more admin features with Atlassian Access.',
        };
      case 'attachment-restrictions':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.attachment-restrictions',
          defaultMessage:
            'Restrict attachment sources across all Workspaces on the Enterprise.',
          description:
            'Restrict attachment sources across all Workspaces on the Enterprise.',
        };
      case 'organization-wide permissions':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.organization-wide permissions',
          defaultMessage:
            'Enforce settings across all Workspaces under the Enterprise.',
          description:
            'Enforce settings across all Workspaces under the Enterprise.',
        };
      case 'organization-visible-boards':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.organization-visible-boards',
          defaultMessage:
            'Create boards that are visible to all members of the Enterprise.',
          description:
            'Create boards that are visible to all members of the Enterprise.',
        };
      case 'public-board-management':
        return {
          id: 'templates.billing_page_one.feature-tooltips-repackaging-gtm.public-board-management',
          defaultMessage:
            'View and change visibility of all public boards within the Enterprise.',
          description:
            'View and change visibility of all public boards within the Enterprise.',
        };
      default:
        return null;
    }
  } else if (categoryKey === 'feature-list-repackaging-gtm') {
    switch (featureKey) {
      case 'collaborators':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.collaborators',
          defaultMessage: 'Collaborators',
          description: 'Collaborators',
        };
      case 'boards':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.boards',
          defaultMessage: 'Boards',
          description: 'Boards',
        };
      case 'unlimited-powerups':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.unlimited-powerups',
          defaultMessage: 'Unlimited Power-Ups',
          description: 'Unlimited Power-Ups',
        };
      case 'automation-1':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.automation-1',
          defaultMessage: 'Automation',
          description: 'Automation',
        };
      case 'file-attachments':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.file-attachments',
          defaultMessage: 'File attachments',
          description: 'File attachments',
        };
      case 'custom-backgrounds':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.custom-backgrounds',
          defaultMessage: 'Custom backgrounds',
          description: 'Custom backgrounds',
        };
      case 'stickers-emojis':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.stickers-emojis',
          defaultMessage: 'Custom stickers and emojis',
          description: 'Custom stickers and emojis',
        };
      case 'advanced-checklists':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.advanced-checklists',
          defaultMessage: 'Advanced checklists',
          description: 'Advanced checklists',
        };
      case 'saved-searches':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.saved-searches',
          defaultMessage: 'Saved searches',
          description: 'Saved searches',
        };
      case 'custom-fields':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.custom-fields',
          defaultMessage: 'Custom fields',
          description: 'Custom fields',
        };
      case 'admin-security-features':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.admin-security-features',
          defaultMessage: 'Admin and security features',
          description: 'Admin and security features',
        };
      case 'board-collections':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.board-collections',
          defaultMessage: 'Board collections',
          description: 'Board collections',
        };
      case 'data-exports':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.data-exports',
          defaultMessage: 'Data exports',
          description: 'Data exports',
        };
      case 'table':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.table',
          defaultMessage: 'Table view',
          description: 'Table view',
        };
      case 'timeline':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.timeline',
          defaultMessage: 'Timeline view',
          description: 'Timeline view',
        };
      case 'calendar':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.calendar',
          defaultMessage: 'Calendar view',
          description: 'Calendar view',
        };
      case 'dashboard':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.dashboard',
          defaultMessage: 'Dashboard view',
          description: 'Dashboard view',
        };
      case 'map':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.map',
          defaultMessage: 'Map view',
          description: 'Map view',
        };
      case 'workspace-board-templates':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.workspace-board-templates',
          defaultMessage: 'Workspace board templates',
          description: 'Workspace board templates',
        };
      case 'observers':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.observers',
          defaultMessage: 'Observers',
          description: 'Observers',
        };
      case 'priority-support':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.priority-support',
          defaultMessage: 'Priority support',
          description: 'Priority support',
        };
      case 'unlimited-workspaces':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.unlimited-workspaces',
          defaultMessage: 'Unlimited Workspaces',
          description: 'Unlimited Workspaces',
        };
      case 'powerup-administration':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.powerup-administration',
          defaultMessage: 'Power-Up administration',
          description: 'Power-Up administration',
        };
      case 'saml-sso':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.saml-sso',
          defaultMessage: 'SAML SSO and user provisioning',
          description: 'SAML SSO and user provisioning',
        };
      case 'attachment-restrictions':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.attachment-restrictions',
          defaultMessage: 'Attachment restrictions',
          description: 'Attachment restrictions',
        };
      case 'organization-wide permissions':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.organization-wide permissions',
          defaultMessage: 'Organization-wide permissions',
          description: 'Organization-wide permissions',
        };
      case 'organization-visible-boards':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.organization-visible-boards',
          defaultMessage: 'Organization visible boards',
          description: 'Organization visible boards',
        };
      case 'public-board-management':
        return {
          id: 'templates.billing_page_one.feature-list-repackaging-gtm.public-board-management',
          defaultMessage: 'Public board management',
          description: 'Public board management',
        };
      default:
        return null;
    }
  } else {
    return null;
  }
};
